import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
} from "@loot-os/ui/components/sidebar";
import { useQuery } from "@tanstack/react-query";
import {
  Link,
  Navigate,
  Outlet,
  useMatches,
  useMatchRoute,
  useNavigate,
  useParams,
} from "@tanstack/react-router";
import { Search } from "lucide-react";
import { useState } from "react";

import { CommandPalette } from "../components/CommandPalette";
import { EnvironmentSwitcher } from "../components/EnvironmentSwitcher";
import { NavSubmenu, NavSubSection } from "../components/NavSubmenu";
import { ScopeSwitcher } from "../components/ScopeSwitcher";
import { ThemeToggle } from "../components/ThemeToggle";
import { useSessionQuery, useSignOutMutation } from "../lib/authQueries";
import { useBoxDraftStore } from "../lib/boxDraftStore";
import { type PlatformTool, platformNavGroups } from "../lib/platformTools";
import { useScopeStore } from "../lib/scopeStore";
import {
  type StorefrontSwitches,
  TENANT_NAV,
  type TenantNavChild,
  type TenantNavItem,
  visibleTenantNav,
} from "../lib/tenantNav";
import { useTRPC } from "../lib/trpc";
import {
  commandPaletteShortcutLabel,
  useCommandPaletteHotkey,
} from "../lib/useCommandPaletteHotkey";
import { useTenantModules } from "../lib/useTenantModules";
import { useTenantRole } from "../lib/useTenantRole";

/**
 * The sidebar in platform scope: the platform tools registry as labelled sections. Kept separate
 * from `TENANT_NAV` (`lib/tenantNav.ts`) rather than merged: these take no route params, and one
 * `to` union spanning both would make `<Link params>` unassignable. Add a tool in
 * `lib/platformTools.ts`, not here.
 */
const PLATFORM_NAV = platformNavGroups();

/**
 * Status dot on a row whose page has one state worth a glance (`status` on its `TENANT_NAV`
 * entry: the Storefront hub, Payments, Battles and Support chat for their switch, Social links
 * for having any network set, Domains for an active custom domain as the primary, Shopify for
 * an active linked shop): green when the active environment has it, muted otherwise. Same query
 * TenantLayout runs for its storefront button, so every dot shares the cache rather than adding
 * a request; the status reads the config row, the domain rows and the Shopify flag beside it.
 */
function SwitchStatusDot({
  label,
  status,
}: {
  label: string;
  status: (config: StorefrontSwitches) => boolean;
}) {
  const trpc = useTRPC();
  const storefront = useQuery(trpc.storefront.get.queryOptions());

  if (!storefront.data) return null;
  const enabled = status({
    ...storefront.data.config,
    domains: storefront.data.domains,
    shopifyLinked: storefront.data.shopifyLinked,
  });
  return (
    <span className="ml-auto flex shrink-0 items-center">
      <span
        className={`size-2 rounded-full ${enabled ? "bg-emerald-500" : "bg-muted-foreground/30"}`}
      />
      <span className="sr-only">{enabled ? `${label} enabled` : `${label} disabled`}</span>
    </span>
  );
}

/** A platform tool's own sidebar link. */
function PlatformToolLink({ tool }: { tool: PlatformTool }) {
  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        tooltip={tool.label}
        render={
          <Link
            to={tool.to}
            activeOptions={{ exact: tool.exact }}
            activeProps={{ "data-active": "true" }}
          />
        }
      >
        <tool.icon />
        <span>{tool.label}</span>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

/** One tenant page: a link, or a hub with its section pages folded beneath it. */
function TenantNavRow({ item, tenantId }: { item: TenantNavItem; tenantId: string }) {
  const matchRoute = useMatchRoute();
  const { can } = useTenantRole();
  const badge = item.status ? (
    <SwitchStatusDot label={item.label} status={item.status} />
  ) : undefined;
  // A section may serve a higher role than its hub; a hub with none left is a plain link.
  const children = (item.children ?? []).filter((child) => can(child.minRole ?? item.minRole));

  if (children.length > 0) {
    const active = matchRoute({ to: item.to, params: { tenantId }, fuzzy: !item.exact }) !== false;
    // A section is current on its own page and the pages below it: Payments on its revenue,
    // Notifications on either of its pages, Gift codes on a batch, Live breaks on its own pages.
    const childActive = (child: TenantNavChild) =>
      matchRoute({ to: child.to, params: { tenantId }, fuzzy: true }) !== false;
    return (
      <NavSubmenu
        label={item.label}
        icon={item.icon}
        active={active}
        badge={badge}
        link={<Link to={item.to} params={{ tenantId }} />}
      >
        {children.map((child) => {
          const dot = child.status ? (
            <SwitchStatusDot label={child.label} status={child.status} />
          ) : undefined;
          // A section with pages of its own (Live breaks): one more fold. Roles and modules are
          // already applied by `visibleTenantNav`.
          if (child.children && child.children.length > 0) {
            return (
              <NavSubSection
                key={child.to}
                label={child.label}
                active={childActive(child)}
                badge={dot}
                link={<Link to={child.to} params={{ tenantId }} />}
              >
                {child.children.map((page) => (
                  <SidebarMenuSubItem key={page.to}>
                    <SidebarMenuSubButton
                      isActive={childActive(page)}
                      render={<Link to={page.to} params={{ tenantId }} />}
                    >
                      <span className="truncate">{page.label}</span>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                ))}
              </NavSubSection>
            );
          }
          return (
            <SidebarMenuSubItem key={child.to}>
              <SidebarMenuSubButton
                isActive={childActive(child)}
                render={<Link to={child.to} params={{ tenantId }} />}
              >
                {/* Named `truncate` itself: the button's last-child rule stops at the dot. */}
                <span className="truncate">{child.label}</span>
                {dot}
              </SidebarMenuSubButton>
            </SidebarMenuSubItem>
          );
        })}
      </NavSubmenu>
    );
  }

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        tooltip={item.label}
        render={
          <Link
            to={item.to}
            params={{ tenantId }}
            activeOptions={{ exact: item.exact }}
            activeProps={{ "data-active": "true" }}
          />
        }
      >
        <item.icon />
        <span>{item.label}</span>
        {badge}
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

function AppShell({
  user,
  onSignOut,
}: {
  user: { name?: string | null; email: string; image?: string | null };
  onSignOut: () => void;
}) {
  // Inside /tenants/$tenantId the sidebar is that tenant's pages; outside, the platform's tools.
  const { tenantId } = useParams({ strict: false }) as { tenantId?: string };
  const { isPlatformAdmin, can } = useTenantRole();
  const [paletteOpen, setPaletteOpen] = useState(false);
  useCommandPaletteHotkey(setPaletteOpen);
  const openPalette = () => setPaletteOpen(true);

  const modules = useTenantModules();
  const tenantNav = tenantId ? visibleTenantNav(TENANT_NAV, { can, modules }) : [];
  // A page names its width on the route (`staticData.pageWidth`). The nearest match wins, so a
  // child can keep Retool's 1560px while its parent fills the window.
  const pageWidth = useMatches({
    select: (matches) => {
      for (let index = matches.length - 1; index >= 0; index -= 1) {
        const width = matches[index]?.staticData.pageWidth;
        if (width) return width;
      }
      return undefined;
    },
  });

  return (
    <SidebarProvider>
      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
      <Sidebar collapsible="icon">
        <SidebarHeader>
          <ScopeSwitcher user={user} onSearch={openPalette} onSignOut={onSignOut} />
        </SidebarHeader>
        <SidebarContent>
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton tooltip="Search" onClick={openPalette}>
                    <Search />
                    <span>Search…</span>
                    <kbd className="ml-auto rounded border border-border bg-muted px-1.5 font-mono text-[10px] text-muted-foreground group-data-[collapsible=icon]:hidden">
                      {commandPaletteShortcutLabel()}
                    </kbd>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                {/* In tenant scope the environment row sits under Search, above the pages it scopes. */}
                {tenantId && <EnvironmentSwitcher tenantId={tenantId} />}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
          {!tenantId &&
            isPlatformAdmin &&
            PLATFORM_NAV.map((section) => (
              <SidebarGroup key={section.key}>
                {section.label && <SidebarGroupLabel>{section.label}</SidebarGroupLabel>}
                <SidebarGroupContent>
                  <SidebarMenu>
                    {section.tools.map((tool) => (
                      <PlatformToolLink key={tool.key} tool={tool} />
                    ))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            ))}
          {tenantId &&
            tenantNav.map((group) => (
              <SidebarGroup key={group.key}>
                {group.label && <SidebarGroupLabel>{group.label}</SidebarGroupLabel>}
                <SidebarGroupContent>
                  <SidebarMenu>
                    {group.items.map((item) => (
                      <TenantNavRow key={item.to} item={item} tenantId={tenantId} />
                    ))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            ))}
        </SidebarContent>
        <SidebarFooter>
          <ThemeToggle />
        </SidebarFooter>
        <SidebarRail />
      </Sidebar>
      <SidebarInset className="min-w-0">
        {/* The default is one centred column. A `full` page has no max width, so its tables
            widen with the window; a `retool` page keeps that app's 1560px. */}
        <div
          className={`mx-auto w-full ${
            pageWidth === "full"
              ? "p-4"
              : pageWidth === "retool"
                ? "max-w-[1560px] p-4"
                : "max-w-6xl p-8"
          }`}
        >
          {/* Inside a tenant, TenantLayout renders the trigger inline with its breadcrumbs. */}
          {!tenantId && <SidebarTrigger className={pageWidth ? "mb-2" : "-mt-4 -ml-4 mb-2"} />}
          <Outlet />
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}

export function AppLayout() {
  const { data: session, isPending, isRefetching } = useSessionQuery();
  const navigate = useNavigate();
  const signOutMutation = useSignOutMutation();

  // A refetch with nothing cached means we don't know yet — waiting beats
  // bouncing to /login on a session that is one round-trip away.
  if (isPending || (isRefetching && !session)) {
    return (
      <div className="flex min-h-svh items-center justify-center text-sm text-muted-foreground">
        Loading…
      </div>
    );
  }
  if (!session) {
    return <Navigate to="/login" />;
  }

  const signOut = async () => {
    // The mutation clears the query cache and seeds the signed-out session.
    await signOutMutation.mutateAsync();
    useScopeStore.getState().clearPersistedScope();
    useBoxDraftStore.getState().clearAll();
    void navigate({ to: "/login" });
  };

  return <AppShell user={session.user} onSignOut={() => void signOut()} />;
}
